import {
  UseMutationOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import APIError from 'types/api_error';
import { Dashboard, DashboardConfiguration } from 'types/dashboard';

import { updateQueriesData } from 'services/update_queries_data';
import { useAPI } from 'hooks/api/useAPI';

type UseUpdateDashboardParams = {
  id: string;
} & (
  | { name: string }
  | { configuration: DashboardConfiguration }
  | { name: string; configuration: DashboardConfiguration }
);

export function useUpdateDashboard({
  onMutate,
  onSettled,
  onError,
  ...options
}: UseMutationOptions<Dashboard, APIError, UseUpdateDashboardParams> = {}) {
  const { applicationID, client } = useAPI();
  const queryClient = useQueryClient();

  return useMutation(
    async ({ id, ...params }) => {
      const { data } = await client.put<Dashboard>(
        `/internal/apps/${applicationID}/dashboards/${id}`,
        { ...params }
      );

      return data;
    },
    {
      ...options,
      onMutate(params) {
        onMutate?.(params);

        return updateQueriesData<Dashboard>({
          queryClient,
          listQueryKey: ['dashboards'],
          singleQueryKey: ['dashboard'],
          ids: [params.id],
          updateData: (dashboard) => {
            if ('name' in params) {
              dashboard.name = params.name;
            }

            if ('configuration' in params) {
              dashboard.configuration = params.configuration;
            }

            return dashboard;
          },
        });
      },
      onError(error, params, context) {
        onError?.(error, params, context);

        context?.previousLists?.forEach(([queryKey, data]) =>
          queryClient.setQueryData(queryKey, data)
        );
        context?.previousSingles?.forEach(([queryKey, data]) =>
          queryClient.setQueryData(queryKey, data)
        );
      },
      onSettled(...args) {
        onSettled?.(...args);
        queryClient.invalidateQueries(['dashboards']);
        queryClient.invalidateQueries(['dashboard']);
      },
    }
  );
}
